import { useMemo } from 'react';

import type { GridColDef } from '@mui/x-data-grid-premium';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  gridColumnDefinitionsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';

/** One choice in the Display Options grouping picker. */
export interface GroupableColumn {
  field: string;
  label: string;
  /**
   * MUI column type, for a column the grid does not define: the group header
   * formats a date column's calendar-day key from it. A grid-backed column
   * leaves this unset and resolves its type from the grid's column lookup.
   */
  type?: GridColDef['type'];
}

/**
 * Grid machinery columns: they render selection and grouping affordances
 * rather than document data, so they can never be grouped on.
 */
const GRID_INTERNAL_FIELDS = new Set<string>([
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
]);

/**
 * The columns a document grid can group its rows by, in the grid's own column
 * order.
 *
 * Derived from the grid's column definitions rather than a hard-coded list, so
 * the report grid contributes its ExtractionQuestion columns and the repository
 * grid its built-in ones without either having to declare anything. A column
 * opts out with `groupable: false` — set from the server's `isGroupable` on
 * report column options, and directly on runtime-only columns such as row
 * status and the add-column affordance.
 */
export function getGroupableColumns(columns: readonly GridColDef[]): GroupableColumn[] {
  return columns.flatMap(column =>
    column.groupable === false || GRID_INTERNAL_FIELDS.has(column.field)
      ? []
      : [{ field: column.field, label: column.headerName || column.field }]
  );
}

/** {@link getGroupableColumns} over the live grid. Grid-context only (e.g. a toolbar slot). */
export function useGroupableColumns(): GroupableColumn[] {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  return useMemo(() => getGroupableColumns(columns), [columns]);
}
